// ============================================================================
// AssistantContactsPanel.tsx — контакты пользователя В ЧАТЕ АССИСТЕНТА.
//
// Зачем: ассистент помогает связаться с людьми. Панель показывает ЕДИНЫЙ
// каталог контактов (сущности платформы + РЕАЛЬНЫЕ adapter_links) и карточку
// выбранного контакта с едиными действиями:
//   Telegram / MAX / внутренний чат / профиль.
//
// Границы: панель НЕ вызывает RPC — только lib/contacts/contactService.
// Открытие переписки — через общий chatNavigation (как остальной чат).
// Дедупликация контактов/каналов — в сервисе (одна карточка на сущность).
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, CircleAlert, RefreshCw, Search, Users, X } from 'lucide-react';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { friendlyError } from '@/lib/errors';
import { openConversation } from '@/lib/chatNavigation';
import {
  contactMatchesQuery,
  loadContactDetails,
  loadContacts,
  openContactChat,
} from '@/lib/contacts/contactService';
import type {
  ContactAction,
  ContactChannelKind,
  ContactDetails,
  ContactSummary,
} from '@/lib/contacts/types';
import { ContactCard } from './ContactCard';
import { ContactProfileDialog } from './ContactProfileDialog';

interface AssistantContactsPanelProps {
  open: boolean;
  onClose: () => void;
  myId: string;
  isAdmin: boolean;
}

type ViewMode = 'list' | 'detail';

function contactSubtitle(contact: ContactSummary): string {
  if (contact.login) return `@${contact.login}`;
  if (contact.phone) return contact.phone;
  if (contact.email) return contact.email;
  return contact.technical ? 'внешний контакт' : 'контакт платформы';
}

function ContactRow({ contact, selected, onClick }: { contact: ContactSummary; selected: boolean; onClick: () => void }) {
  const channels = contact.channels.filter((channel) => channel.kind !== 'internal');
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition-colors ${
        selected ? 'border-primary/50 bg-primary/10' : 'border-border/60 hover:border-primary/30 hover:bg-accent/40'
      }`}
    >
      <UserAvatar uid={contact.id} fileId={contact.avatarUrl} name={contact.name} size={34} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12px] font-medium leading-tight">{contact.name}</span>
        <span className="block truncate text-[10px] text-muted-foreground">{contactSubtitle(contact)}</span>
      </span>
      <span className="flex shrink-0 items-center gap-1">
        {channels.slice(0, 2).map((channel) => (
          <span
            key={`${channel.kind}:${channel.value}`}
            className={`rounded-full border px-1.5 py-0.5 text-[9px] ${
              channel.source === 'adapter'
                ? 'border-primary/25 bg-primary/5 text-primary'
                : 'border-border bg-muted/40 text-muted-foreground'
            }`}
            title={channel.label}
          >
            {channel.kind === 'telegram' ? 'TG' : 'MAX'}
          </span>
        ))}
      </span>
    </button>
  );
}

export function AssistantContactsPanel({ open, onClose, myId, isAdmin }: AssistantContactsPanelProps) {
  const { isMobile } = useBreakpoint();

  const [contacts, setContacts] = useState<ContactSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [view, setView] = useState<ViewMode>('list');
  const [details, setDetails] = useState<ContactDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [detailsTick, setDetailsTick] = useState(0);
  const [busyKind, setBusyKind] = useState('');
  const [actionError, setActionError] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);

  const loadDirectory = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const items = await loadContacts({ myId, canReadAdapterLinks: isAdmin });
      setContacts(items);
      setSelectedId((prev) => (prev && items.some((item) => item.id === prev) ? prev : ''));
      // Панель ОТКРЫВАЕТСЯ СПИСКОМ (вся ширина — каталог), карточка — по клику.
      setView('list');
    } catch (error) {
      setContacts([]);
      setSelectedId('');
      setListError(friendlyError(error));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, myId]);

  // Закрытие панели: сбрасываем временные состояния (поиск/ошибку/профиль),
  // чтобы повторное открытие начиналось с чистого каталога.
  const closePanel = useCallback(() => {
    setQuery('');
    setActionError('');
    setProfileOpen(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    // Загрузка — асинхронная операция эффекта; setState происходит в её
    // колбэках, а не синхронно в теле эффекта.
    const run = async () => { await loadDirectory(); };
    void run();
  }, [open, loadDirectory]);

  // Esc закрывает панель (как остальные окна платформы).
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') closePanel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closePanel]);

  const selected = useMemo(
    () => contacts.find((contact) => contact.id === selectedId) || null,
    [contacts, selectedId],
  );

  useEffect(() => {
    if (!open || !selected) return;
    let alive = true;
    const load = async () => {
      setDetailsLoading(true);
      setDetailsError('');
      setActionError('');
      try {
        const result = await loadContactDetails(selected, { myId, canReadAdapterLinks: isAdmin });
        if (alive) setDetails(result);
      } catch (error) {
        if (alive) {
          setDetails(null);
          setDetailsError(friendlyError(error));
        }
      } finally {
        if (alive) setDetailsLoading(false);
      }
    };
    void load();
    return () => { alive = false; };
  }, [open, selected, myId, isAdmin, detailsTick]);

  const filtered = useMemo(
    () => contacts.filter((contact) => contactMatchesQuery(contact, query)),
    [contacts, query],
  );

  const openContact = (id: string) => {
    setSelectedId(id);
    setView('detail');
  };

  const handleAction = async (action: ContactAction) => {
    if (!details) return;
    if (action.kind === 'profile') {
      setProfileOpen(true);
      return;
    }
    setBusyKind(action.kind);
    setActionError('');
    try {
      const result = await openContactChat(details, action.kind as ContactChannelKind);
      closePanel();
      openConversation(result.conversationId);
    } catch (error) {
      setActionError(friendlyError(error));
    } finally {
      setBusyKind('');
    }
  };

  // ПОЛНАЯ ШИРИНА области чата (desktop) / экрана (мобильный): панель не
  // сжимает ленту и не обрезает шапку — X закрытия всегда на виду.
  const panelClass = isMobile
    ? 'fixed inset-0 z-[12500] flex flex-col bg-sidebar pb-[env(safe-area-inset-bottom)]'
    : 'absolute inset-0 z-[12500] flex flex-col overflow-hidden bg-sidebar shadow-2xl';

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          key="assistant-contacts-panel"
          role="dialog"
          aria-label="Контакты"
          className={panelClass}
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'tween', duration: 0.18 }}
        >
          <div className="flex h-full min-h-0 w-full flex-col">
            <div className="flex shrink-0 items-center gap-2 border-b px-3 py-2 glass-panel rounded-none border-x-0 border-t-0">
              {view === 'detail' && contacts.length > 0 ? (
                <button
                  type="button"
                  className="-ml-1 rounded-full p-1.5 hover:bg-muted/40"
                  onClick={() => setView('list')}
                  aria-label="К списку контактов"
                  title="К списку контактов"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              ) : (
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Users className="h-4 w-4" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold leading-none">Контакты</div>
                <div className="mt-0.5 truncate text-[10px] text-muted-foreground">
                  {view === 'detail' && selected ? selected.name : 'Единый каталог: профиль, Telegram, MAX'}
                </div>
              </div>
              <button
                type="button"
                className="shrink-0 rounded-full p-1.5 hover:bg-muted/50"
                onClick={closePanel}
                aria-label="Закрыть контакты"
                title="Закрыть"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-2.5 space-y-2.5">
              {loading && (
                <div className="space-y-2">
                  {[0, 1, 2].map((item) => (
                    <div key={item} className="h-14 animate-pulse rounded-xl border bg-muted/40" />
                  ))}
                </div>
              )}

              {!loading && listError && (
                <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-[11px] text-destructive">
                  <div className="flex items-start gap-1.5">
                    <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span className="min-w-0 flex-1 break-words">{listError}</span>
                  </div>
                  <button type="button" className="mt-1.5 underline" onClick={() => void loadDirectory()}>Повторить</button>
                </div>
              )}

              {!loading && !listError && contacts.length === 0 && (
                <div className="flex flex-col items-center gap-1 py-8 text-center text-muted-foreground">
                  <Users className="h-6 w-6 opacity-40" />
                  <div className="text-[11px]">Контактов пока нет</div>
                  <div className="text-[10px]">Добавьте пользователей или подключите Telegram/MAX в профиле.</div>
                </div>
              )}

              {!loading && !listError && contacts.length > 0 && view === 'list' && (
                <>
                  <div className="relative shrink-0">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <input
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="Поиск по имени, логину, телефону…"
                      aria-label="Поиск контакта"
                      className="h-8 w-full rounded-full border bg-muted/30 pl-8 pr-3 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary/40 placeholder:text-muted-foreground/70"
                    />
                  </div>
                  {filtered.length === 0 ? (
                    <div className="py-6 text-center text-[11px] text-muted-foreground">Никого не найдено</div>
                  ) : (
                    <div className="space-y-1">
                      {filtered.map((contact) => (
                        <ContactRow
                          key={contact.id}
                          contact={contact}
                          selected={contact.id === selectedId}
                          onClick={() => openContact(contact.id)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {!loading && !listError && view === 'detail' && selected && (
                <>
                  {detailsLoading && (
                    <div className="glass-panel rounded-xl p-3 space-y-2 animate-pulse">
                      <div className="h-10 w-40 rounded bg-muted" />
                      <div className="h-3 w-full rounded bg-muted" />
                      <div className="h-3 w-2/3 rounded bg-muted" />
                    </div>
                  )}
                  {!detailsLoading && detailsError && (
                    <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-[11px] text-destructive">
                      <div className="flex items-start gap-1.5">
                        <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span className="min-w-0 flex-1 break-words">{detailsError}</span>
                      </div>
                      <div className="mt-1.5 flex gap-2">
                        <button type="button" className="underline" onClick={() => setDetailsTick((value) => value + 1)}>
                          Повторить
                        </button>
                        <button type="button" className="underline" onClick={() => setView('list')}>
                          К списку
                        </button>
                      </div>
                    </div>
                  )}
                  {!detailsLoading && !detailsError && details && (
                    <ContactCard
                      details={details}
                      busyKind={busyKind}
                      onAction={(action) => void handleAction(action)}
                      onRetry={() => setDetailsTick((value) => value + 1)}
                    />
                  )}
                  {actionError && (
                    <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-2 text-[10px] text-destructive">
                      {actionError}
                    </div>
                  )}
                  <button
                    type="button"
                    className="flex w-full items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-[10px] text-muted-foreground hover:bg-accent/40"
                    onClick={() => void loadDirectory()}
                  >
                    <RefreshCw className="h-3 w-3" />Обновить каталог
                  </button>
                </>
              )}
            </div>
          </div>

          {profileOpen && details && (
            <ContactProfileDialog
              details={details}
              isAdmin={isAdmin}
              busyKind={busyKind}
              onAction={(action) => void handleAction(action)}
              onClose={() => setProfileOpen(false)}
            />
          )}
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
